import { AxiosResponse } from 'axios';

import { SECURE_API } from 'src/api/api';
import { FormTechnicalType } from 'src/common/forms/TechnicalTypeEditorForm';
import { TechnicalTypeCrudConfig } from 'src/stores/config/technicalTypesCrud';
import { ListAction, listReducer } from 'src/stores/lib/listReducer';
import { runApiAction } from 'src/stores/lib/runApiAction';
import { StoreGetter, StoreSetter } from 'src/stores/lib/types';

type TechnicalTypeItem = { id: string; name: string };

class TechnicalTypeCrudImpl<TStore extends Record<string, unknown>, TItem extends TechnicalTypeItem> {
  readonly #set: StoreSetter<TStore>;
  readonly #config: TechnicalTypeCrudConfig<TItem>;

  constructor(set: StoreSetter<TStore>, _get: StoreGetter<TStore>, config: TechnicalTypeCrudConfig<TItem>) {
    this.#set = set;
    this.#config = config;
  }

  finishSetup() {
    this.#bindPublicActions();
    this.#bindDispatchAction();
  }

  #bindPublicActions() {
    const { actions } = this.#config;
    const target = this as Record<string, unknown>;

    target[actions.fetch] = this.fetch.bind(this);
    target[actions.save] = this.save.bind(this);
    target[actions.delete] = this.delete.bind(this);
    target[actions.deleteAll] = this.deleteAll.bind(this);
    target[actions.reset] = this.reset.bind(this);
  }

  #bindDispatchAction() {
    (this as Record<string, unknown>)[this.#config.actions.dispatch] = this.internal_dispatch.bind(this);
  }

  fetch = async (): Promise<void | AxiosResponse<TItem[]>> => {
    this.#set({ isLoading: true } as unknown as Partial<TStore>);
    return this.internal_fetch();
  };

  save = async (
    item: FormTechnicalType,
    id?: string,
  ): Promise<void | AxiosResponse<TItem[]>> => {
    this.#set({ isLoading: true } as unknown as Partial<TStore>);
    return this.internal_save(item, id);
  };

  delete = async (id: string): Promise<void | AxiosResponse<null>> => {
    this.#set({ isLoading: true } as unknown as Partial<TStore>);
    return this.internal_delete(id);
  };

  deleteAll = async (): Promise<void | AxiosResponse<null>> => {
    this.#set({ isLoading: true } as unknown as Partial<TStore>);
    return this.internal_deleteAll();
  };

  reset = () => {
    this.internal_dispatch({ type: 'reset' });
  };

  internal_fetch = async (): Promise<void | AxiosResponse<TItem[]>> => {
    const { paths, stateKeys } = this.#config;

    return runApiAction<TStore, AxiosResponse<TItem[]>>({
      set: this.#set,
      action: () => SECURE_API.get<TItem[]>(paths.list),
      onSuccess: ({ data }) => {
        this.internal_dispatch({ type: 'setList', items: data });
        this.#set({ isLoading: false } as unknown as Partial<TStore>);
      },
      onError: () => {
        this.#set({
          [stateKeys.list]: [],
          [stateKeys.byId]: undefined,
          [stateKeys.byName]: undefined,
          isLoading: false,
        } as unknown as Partial<TStore>);
      },
    });
  };

  internal_save = async (
    item: FormTechnicalType,
    id?: string,
  ): Promise<void | AxiosResponse<TItem[]>> => {
    const { paths } = this.#config;
    const savePromise = id
      ? SECURE_API.put<TItem[]>(paths.update(id), item)
      : SECURE_API.post<TItem[]>(paths.save, item);

    return savePromise
      .catch((error: unknown) => {
        console.log({ error });
      })
      .finally(() => {
        void this.internal_fetch();
      });
  };

  internal_delete = async (id: string): Promise<void | AxiosResponse<null>> => {
    const { paths } = this.#config;

    return SECURE_API.delete<null>(paths.delete(id)).finally(() => {
      void this.internal_fetch();
    });
  };

  internal_deleteAll = async (): Promise<void | AxiosResponse<null>> => {
    const { paths } = this.#config;

    return SECURE_API.delete<null>(paths.bulkDelete).finally(() => {
      void this.internal_fetch();
    });
  };

  internal_dispatch = (action: ListAction<TItem>) => {
    this.#set(state => ({
      ...state,
      ...listReducer(state as Record<string, unknown>, action, this.#config.stateKeys),
    }));
  };
}

export const createTechnicalTypeCrudAction = <
  TStore extends Record<string, unknown>,
  TItem extends TechnicalTypeItem,
>(
  set: StoreSetter<TStore>,
  get: StoreGetter<TStore>,
  config: TechnicalTypeCrudConfig<TItem>,
) => {
  const impl = new TechnicalTypeCrudImpl<TStore, TItem>(set, get, config);
  impl.finishSetup();
  return impl;
};
